import React, { useCallback, useEffect, useRef, useState } from "react";
import { RouteComponentProps } from "react-router-dom";
import { Container, Header, Icon, Segment } from "semantic-ui-react";
import { toast } from "react-toastify";

import TemplateTable from "../../components/TemplateTable";
import { ITableAppRender } from "../../components/TableApp";
import { IFields } from "../../components/Search/FilterBuilder";
import { ISearch } from "../../components/Search";
import { ISearchResponse } from "../../interfaces";
import errors from "../../Utils/ExtractErrors";
import NotificationRepository, { IRecebedorListModel } from "../../Repositories/NotificationRepository";
import { format, formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";

interface IProps {
    id: string;
}

interface IState {
    loading: boolean;
    tableLoading: boolean;
    data: string;
    description: string;
    message: string;
    list: IRecebedorListModel[];
}

const initialState: IState = {
    loading: false,
    tableLoading: false,
    data: "",
    description: "",
    message: "",
    list: []
}

export default function AdminStatusNotificacao(props: RouteComponentProps<IProps>) {
    const [state, setState] = useState<IState>(initialState);

    const refSearch = useRef<ISearch>({ searchString: "", type: "simples" });
    const refRecordsByPage = useRef<number>(20);
    const refPageNumber = useRef<string | number | undefined>(1);
    const refTotalRecords = useRef<number>(0);
    const refTotalPages = useRef<number>(1);

    const loadNotification = useCallback(async () => {
        try {
            setState(state => ({ ...state, loading: true }));
            const [notification, receivers] = await Promise.all([
                NotificationRepository.GetById({
                    id: props.match.params.id,
                    includeRecebedores: false
                }),
                NotificationRepository.ListReceivers(
                    props.match.params.id,
                    {
                        search: refSearch.current.searchString,
                        pageNumber: refPageNumber.current,
                        recordsByPage: refRecordsByPage.current
                    }
                )
            ]);

            refTotalRecords.current = receivers.totalRecords;
            refTotalPages.current = receivers.totalPages;

            setState(state => ({
                ...state,
                data: format(new Date(notification.criadaEm), "dd/MM/yyyy HH:mm", { locale: ptBR }),
                description: notification.descricao,
                message: notification.mensagem,
                list: receivers.list,
                loading: false
            }));
        } catch (err) {
            if (err.response.status === 401) {
                window.location.href = "/";
            }

            const error = errors.getAllErrors(err.response.data);

            if (error !== undefined)
                toast(error, { position: "top-center", type: "error" });

            setState(state => ({ ...state, loading: false }));
        }
    }, [props.match.params.id]);

    async function load() {
        setState(state => ({ ...state, tableLoading: true }));
        try {
            let response: ISearchResponse<IRecebedorListModel>;

            if (refSearch.current.type === "simples") {
                response = await NotificationRepository.ListReceivers(
                    props.match.params.id,
                    {
                        search: refSearch.current.searchString,
                        pageNumber: refPageNumber.current,
                        recordsByPage: refRecordsByPage.current
                    }
                );
            } else {
                response = await NotificationRepository.ListReceivers(
                    props.match.params.id,
                    {
                        filter: refSearch.current.searchString,
                        pageNumber: refPageNumber.current,
                        recordsByPage: refRecordsByPage.current
                    }
                );
            }

            refTotalRecords.current = response.totalRecords;
            refTotalPages.current = response.totalPages;

            setState(state => ({ ...state, tableLoading: false, list: response.list }));
        } catch (err) {
            if (err.response.status === 401) {
                window.location.href = "/";
            }

            const error = errors.getAllErrors(err.response.data);

            if (error !== undefined)
                toast(error, { position: "top-center", type: "error" });

            setState(state => ({ ...state, tableLoading: false }));
        }
    }

    function onChangeRecords(value: number) {
        refRecordsByPage.current = value;
        load();
    }

    function onPageChange(pageNumber: string | number | undefined) {
        refPageNumber.current = pageNumber;
        load();
    }

    function onSearch({ searchString, type }: ISearch) {
        refSearch.current.searchString = searchString;
        refSearch.current.type = type;
        load();
    }

    function formatBooleanToDistance(value: boolean, date: string) {
        if (value) {
            return formatDistanceToNow(new Date(date), { addSuffix: true, locale: ptBR });
        } else {
            return <Icon name="times" color="red" />
        }
    }

    const tableRender: ITableAppRender[] = [
        {
            header: "Recebedor",
            content: (recebedor: IRecebedorListModel) => recebedor.recebedor,
            textAlign: "left"
        },
        {
            header: "Visualizada",
            content: (recebedor: IRecebedorListModel) => formatBooleanToDistance(recebedor.visualizada, recebedor.visualizadaEm),
            textAlign: "center"
        },
        {
            header: "Removida",
            content: (recebedor: IRecebedorListModel) => formatBooleanToDistance(recebedor.removida, recebedor.removidaEm),
            textAlign: "center"
        },
    ];

    const fields: IFields[] = [
        { label: "Recebedor", name: "Recebedor", type: "string", roles: ["Admin"] },
        {
            label: "Visualizada",
            name: "Visualizada",
            type: "enum",
            values: [
                { key: "TRUE", text: "Visualizada", value: true, label: { icon: "check", color: "pink", content: "VIS" } },
                { key: "FALSE", text: "Não visuali.", value: false, label: { icon: "times", color: "grey", content: "NVI" } },
            ],
            roles: ["Admin"]
        },
        { label: "Visualizada em", name: "VisualizadaEm", type: "date", roles: ["Admin"] },
        {
            label: "Removida",
            name: "Removida",
            type: "enum",
            values: [
                { key: "TRUE", text: "Removida", value: true, label: { icon: "check", color: "pink", content: "REM" } },
                { key: "FALSE", text: "Não remov.", value: false, label: { icon: "times", color: "grey", content: "NRE" } },
            ],
            roles: ["Admin"]
        },
        { label: "Removida em", name: "RemovidaEm", type: "date", roles: ["Admin"] },
    ];

    const content = (
        <Segment color="pink">
            <Container textAlign="justified">
                <Header
                    dividing
                    size="small"
                    content={state.description}
                    subheader={`Enviada em: ${state.data}`}
                />
                <p>
                    {state.message}
                </p>
            </Container>
        </Segment>
    );

    useEffect(() => {
        loadNotification();
    }, [loadNotification]);

    return (
        <TemplateTable
            title="notificação status"
            icon="info"
            loading={state.loading}
            content={content}
            onSearch={onSearch}
            searchPlaceHolder="Recebedor"
            filterFields={fields}
            tableLoading={state.tableLoading}
            tableRender={tableRender}
            tableList={state.list}
            tableOnChangeRecords={onChangeRecords}
            tablePagination={{
                currentPage: refPageNumber.current,
                totalRecords: refTotalRecords.current,
                totalPages: refTotalPages.current,
                onPageChange: onPageChange
            }}
        />
    )
}
